<template>
  <div class="balance-star" :style="size ? { '--bs-size': `${size}px` } : undefined" aria-hidden="true">
    <svg class="balance-star__svg" viewBox="0 0 200 200" focusable="false">
      <defs>
        <radialGradient :id="`${uid}-core`" cx="50%" cy="52%" r="50%">
          <stop offset="0%" stop-color="#DDF55A" stop-opacity="0.24" />
          <stop offset="45%" stop-color="#DDF55A" stop-opacity="0.08" />
          <stop offset="100%" stop-color="#DDF55A" stop-opacity="0" />
        </radialGradient>
        <radialGradient :id="`${uid}-fill`" cx="40%" cy="30%" r="78%">
          <stop offset="0%" stop-color="#FDFFDC" />
          <stop offset="26%" stop-color="#F0FC8C" />
          <stop offset="62%" stop-color="#DBF24F" />
          <stop offset="100%" stop-color="#A7C626" />
        </radialGradient>
        <linearGradient :id="`${uid}-gloss`" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="#FFFFFF" stop-opacity="0.85" />
          <stop offset="100%" stop-color="#FFFFFF" stop-opacity="0" />
        </linearGradient>
        <filter :id="`${uid}-glow`" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="9" />
        </filter>
        <filter :id="`${uid}-soft`" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="2.6" />
        </filter>
      </defs>

      <!-- фон: свечение и тонкие кольца -->
      <circle cx="100" cy="100" r="99" :fill="`url(#${uid}-core)`" />
      <circle cx="100" cy="100" r="93" fill="none" stroke="#DDF55A" stroke-opacity="0.1" stroke-width="1" />
      <circle cx="100" cy="100" r="66" fill="#000" fill-opacity="0.2" stroke="#FFFFFF" stroke-opacity="0.05" stroke-width="1" />

      <!-- частичные лаймовые дуги -->
      <g class="balance-star__arcs">
        <g v-for="arc in ARCS" :key="arc.rotate" :transform="`rotate(${arc.rotate} 100 100)`">
          <circle
            cx="100"
            cy="100"
            r="81"
            fill="none"
            stroke="#DDF55A"
            stroke-width="8"
            stroke-linecap="round"
            :stroke-opacity="arc.opacity * 0.55"
            :stroke-dasharray="arc.dash"
            :filter="`url(#${uid}-soft)`"
          />
          <circle
            cx="100"
            cy="100"
            r="81"
            fill="none"
            stroke="#DDF55A"
            :stroke-width="arc.width"
            stroke-linecap="round"
            :stroke-opacity="arc.opacity"
            :stroke-dasharray="arc.dash"
          />
        </g>
      </g>

      <!-- звезда: свечение, «толщина», тело, блик -->
      <g class="balance-star__star">
        <path :d="STAR" fill="#DDF55A" fill-opacity="0.7" :filter="`url(#${uid}-glow)`" />
        <path
          :d="STAR"
          transform="translate(0 5)"
          fill="#7E9A12"
          stroke="#7E9A12"
          stroke-width="13"
          stroke-linejoin="round"
        />
        <path
          :d="STAR"
          :fill="`url(#${uid}-fill)`"
          :stroke="`url(#${uid}-fill)`"
          stroke-width="13"
          stroke-linejoin="round"
        />
        <ellipse
          cx="84"
          cy="80"
          rx="17"
          ry="7.5"
          transform="rotate(-32 84 80)"
          :fill="`url(#${uid}-gloss)`"
          opacity="0.75"
        />
      </g>

      <!-- искры -->
      <circle
        v-for="(spark, index) in SPARKS"
        :key="index"
        class="balance-star__spark"
        :style="{ animationDelay: `${spark.delay}s` }"
        :cx="spark.x"
        :cy="spark.y"
        :r="spark.r"
        fill="#F2FF9E"
      />
    </svg>
  </div>
</template>

<script setup>
/**
 * BalanceStar — большая глянцевая светящаяся звезда с частичными лаймовыми дугами (экран 10, «Баланс»).
 * Чисто декоративная (aria-hidden). Анимации отключаются при prefers-reduced-motion.
 *
 * <BalanceStar :size="176" />
 * <BalanceStar class="my-star" />      // размер из CSS родителя: .my-star { --bs-size: min(46vw, 200px) }
 *
 * props: size (px; без него — CSS-переменная --bs-size, по умолчанию 176px).
 */
defineProps({
  size: { type: Number, default: null },
});

const uid = `bs${Math.random().toString(36).slice(2, 9)}`;

// Пятиконечная звезда с центром (100, 106): R = 48, r = 24 (скругление даёт stroke-linejoin).
const STAR =
  "M100 58L114.11 86.58 145.65 91.17 122.83 113.42 128.21 144.83 100 130 71.79 144.83 77.17 113.42 54.35 91.17 85.89 86.58Z";

const CIRCUMFERENCE = 2 * Math.PI * 81;
const dash = (deg) => {
  const len = (CIRCUMFERENCE * deg) / 360;
  return `${len.toFixed(1)} ${(CIRCUMFERENCE - len).toFixed(1)}`;
};

const ARCS = [
  { rotate: 188, dash: dash(84), width: 4, opacity: 1 },
  { rotate: 22, dash: dash(70), width: 3.5, opacity: 0.9 },
  { rotate: 300, dash: dash(26), width: 2, opacity: 0.35 },
];

const SPARKS = [
  { x: 154, y: 58, r: 1.8, delay: 0 },
  { x: 40, y: 132, r: 1.4, delay: 0.8 },
  { x: 140, y: 152, r: 1.2, delay: 1.6 },
  { x: 58, y: 50, r: 1.1, delay: 2.2 },
];
</script>

<style scoped>
.balance-star {
  position: relative;
  flex-shrink: 0;
  width: var(--bs-size, 176px);
  height: var(--bs-size, 176px);
  max-width: 100%;
}

.balance-star__svg {
  width: 100%;
  height: 100%;
  overflow: visible;
}

.balance-star__arcs {
  transform-box: view-box;
  transform-origin: 50% 50%;
  animation: cb-spin 28s linear infinite;
}

.balance-star__star {
  transform-box: view-box;
  transform-origin: 50% 50%;
  animation: balance-star-float 4.2s ease-in-out infinite;
}

.balance-star__spark {
  animation: balance-star-twinkle 3s ease-in-out infinite;
}

@keyframes balance-star-float {
  0%,
  100% {
    transform: translateY(0) scale(1);
  }
  50% {
    transform: translateY(-3px) scale(1.015);
  }
}

@keyframes balance-star-twinkle {
  0%,
  100% {
    opacity: 0.2;
  }
  50% {
    opacity: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .balance-star__arcs,
  .balance-star__star,
  .balance-star__spark {
    animation: none !important;
  }
}
</style>
